import { useEffect, useRef, useState } from 'react';
import { useChatStore } from '../../store/chat';

/** Formata segundos como mm:ss (ex.: 75 -> "01:15"). */
function formatElapsed(totalSeconds: number): string {
  const mm = Math.floor(totalSeconds / 60);
  const ss = totalSeconds % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

/** Lê um Blob/File como data-URL base64 (ex.: "data:image/png;base64,..."). */
function readBlobAsDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/** Gravação por microfone só é possível com MediaRecorder + getUserMedia. */
function isRecordingSupported(): boolean {
  return (
    typeof MediaRecorder !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia
  );
}

export function MessageInput() {
  const [value, setValue] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const audioInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  // Quando true, o onstop descarta o áudio (gravação cancelada) em vez de enviar.
  const canceledRef = useRef(false);

  // Timer mm:ss enquanto grava.
  useEffect(() => {
    if (!isRecording) return;
    setElapsed(0);
    const id = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [isRecording]);

  function handleSubmit() {
    const text = value.trim();
    if (!text) return;
    useChatStore.getState().sendMessage(text);
    setValue('');
  }

  /**
   * Comprime a foto no CLIENTE antes de enviar (máx 1600px, JPEG q0.82):
   * (1) foto de celular tem 3-12MB — em base64 estoura upload/latência;
   * (2) iPhone manda HEIC, que o OCR (Vision) NÃO aceita — o canvas re-encoda
   * pra JPEG universal. Sem suporte a canvas/bitmap (jsdom, browsers antigos),
   * cai no data-URL original sem quebrar nada.
   */
  async function compressImageBlob(blob: Blob): Promise<string> {
    const original = await readBlobAsDataUrl(blob);
    try {
      if (typeof createImageBitmap !== 'function') return original;
      const bmp = await createImageBitmap(blob);
      const MAX = 1600;
      const scale = Math.min(1, MAX / Math.max(bmp.width, bmp.height));
      const w = Math.max(1, Math.round(bmp.width * scale));
      const h = Math.max(1, Math.round(bmp.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return original;
      ctx.drawImage(bmp, 0, 0, w, h);
      bmp.close?.();
      const jpeg = canvas.toDataURL('image/jpeg', 0.82);
      // toDataURL pode falhar silenciosamente (retorna "data:,") — valida antes de usar.
      return jpeg.startsWith('data:image/jpeg') && jpeg.length > 100 ? jpeg : original;
    } catch {
      return original;
    }
  }

  /** Lê uma imagem (Blob), comprime, e envia pelo caminho de anexo padrão. */
  async function sendImageBlob(blob: Blob) {
    const dataUrl = await compressImageBlob(blob);
    useChatStore.getState().sendPayload({ tipo: 'imagem', conteudo: dataUrl });
  }

  async function handleImagePicked(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Allow re-picking the same file by resetting the input value.
    e.target.value = '';
    if (!file) return;
    if (file.type === 'application/pdf') {
      const dataUrl = await readBlobAsDataUrl(file);
      useChatStore.getState().sendPayload({
        tipo: 'pdf',
        conteudo: dataUrl,
        mime: 'application/pdf',
        nome: file.name,
      });
      return;
    }
    await sendImageBlob(file);
  }

  /**
   * Colar imagem com Ctrl-V: se o clipboard contiver um item de imagem, lê o
   * blob como data-URL e envia pelo mesmo caminho do anexo (tipo:"imagem").
   */
  function handlePaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          // Impede que a imagem vire texto/lixo no campo de mensagem.
          e.preventDefault();
          void sendImageBlob(file);
          return;
        }
      }
    }
  }

  async function sendAudioBlob(blob: Blob, mime: string) {
    // envia o data-URL COMPLETO (com prefixo) — igual a imagem/pdf. O backend só decodifica
    // áudio quando o conteúdo começa com "data:"; mandar base64 cru caía no ramo de "baixar URL"
    // e o áudio nunca chegava ao Whisper.
    const dataUrl = await readBlobAsDataUrl(blob);
    useChatStore.getState().sendPayload({ tipo: 'audio', conteudo: dataUrl, mime });
  }

  async function handleAudioPicked(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    await sendAudioBlob(file, file.type || 'audio/mpeg');
  }

  async function startRecording() {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const recorder = new MediaRecorder(stream);
    chunksRef.current = [];
    canceledRef.current = false;
    recorder.ondataavailable = (ev) => {
      if (ev.data.size > 0) chunksRef.current.push(ev.data);
    };
    recorder.onstop = () => {
      const mime = recorder.mimeType || 'audio/webm';
      const blob = new Blob(chunksRef.current, { type: mime });
      stream.getTracks().forEach((t) => t.stop());
      // Só envia se a gravação não foi cancelada.
      if (!canceledRef.current) void sendAudioBlob(blob, mime);
      setIsRecording(false);
    };
    recorderRef.current = recorder;
    recorder.start();
    setIsRecording(true);
  }

  /** Para a gravação e ENVIA o áudio. */
  function stopRecording() {
    canceledRef.current = false;
    recorderRef.current?.stop();
    recorderRef.current = null;
  }

  /** Para a gravação e DESCARTA o áudio (botão de cancelar). */
  function cancelRecording() {
    canceledRef.current = true;
    recorderRef.current?.stop();
    recorderRef.current = null;
  }

  function handleMicClick() {
    // No browser support (e.g. jsdom / older browsers) -> fall back to attaching a file.
    if (!isRecordingSupported()) {
      audioInputRef.current?.click();
      return;
    }
    if (isRecording) {
      stopRecording();
    } else {
      void startRecording();
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }

  const isEmpty = value.trim() === '';

  return (
    <div
      className="flex items-end gap-1.5 px-2.5 pt-1.5 flex-shrink-0"
      style={{ paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom))' }}
    >
      {/* Hidden inputs (sempre montados): anexo de imagem/PDF + fallback de áudio. */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*,application/pdf"
        aria-label="Anexar imagem"
        className="hidden"
        onChange={handleImagePicked}
      />
      <input
        ref={audioInputRef}
        type="file"
        accept="audio/*"
        aria-label="Anexar áudio"
        className="hidden"
        onChange={handleAudioPicked}
      />
      {/* Câmera direta (celular): capture=environment abre a câmera traseira na hora,
          igual ao ícone de câmera do WhatsApp mobile. */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        aria-label="Tirar foto da receita"
        className="hidden"
        onChange={handleImagePicked}
      />

      {isRecording ? (
        /* ── Barra de gravação: cancelar (X) · ondas + timer · enviar (✓) ── */
        <div role="status" aria-label="Gravando áudio" className="flex-1 flex items-center gap-2">
          {/* Cancelar (descarta) */}
          <button
            type="button"
            aria-label="Cancelar gravação"
            onClick={cancelRecording}
            className="w-9 h-9 flex items-center justify-center rounded-full text-[#54656F] hover:text-[#E53935] hover:bg-[#0000000a] transition-colors flex-shrink-0"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>

          {/* Mic pulsante + ondas + timer */}
          <div className="flex-1 flex items-center gap-3 bg-white rounded-full px-4 h-[44px] shadow-[0_1px_2px_rgba(0,0,0,0.06)] border border-black/[0.05]">
            <svg
              className="wa-rec-pulse text-[#E53935] flex-shrink-0"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.91-3c-.49 0-.9.36-.98.85C16.52 14.2 14.47 16 12 16s-4.52-1.8-4.93-4.15c-.08-.49-.49-.85-.98-.85-.61 0-1.09.54-1 1.14.49 3 2.89 5.35 5.91 5.78V20c0 .55.45 1 1 1s1-.45 1-1v-2.08c3.02-.43 5.42-2.78 5.91-5.78.1-.6-.39-1.14-1-1.14z" />
            </svg>
            {/* Waveform (puro feedback visual — NÃO lê o áudio real). */}
            <div className="flex items-center gap-[3px] h-5" aria-hidden="true">
              {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                <span
                  key={i}
                  className="wa-wave-bar w-[3px] h-4 rounded-full bg-[#1DAB61]"
                />
              ))}
            </div>
            <span className="ml-auto text-[14px] tabular-nums text-[#54656F]">
              {formatElapsed(elapsed)}
            </span>
          </div>

          {/* Enviar (✓) */}
          <button
            type="button"
            aria-label="Enviar gravação"
            onClick={stopRecording}
            className="w-11 h-11 flex items-center justify-center rounded-full text-white bg-[#1DAB61] hover:bg-[#179552] transition-colors flex-shrink-0 shadow-[0_1px_3px_rgba(0,0,0,0.15)]"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
            </svg>
          </button>
        </div>
      ) : (
        <>
          {/* "+" de anexos (design novo: à esquerda, fora do campo) */}
          <button
            type="button"
            aria-label="Anexar arquivo"
            onClick={() => imageInputRef.current?.click()}
            className="w-10 h-11 flex items-center justify-center text-[#0A0A0A] active:opacity-50 transition-opacity flex-shrink-0"
          >
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
              <path d="M12 5v14M5 12h14" />
            </svg>
          </button>

          {/* Campo pill com figurinha/emoji DENTRO (à direita), como no WhatsApp novo */}
          <div className="flex-1 flex items-center bg-white rounded-full border border-black/[0.05] shadow-[0_1px_2px_rgba(0,0,0,0.05)] pl-4 pr-2 min-h-[44px]">
            <textarea
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder="Mensagem"
              rows={1}
              className="flex-1 text-[16px] md:text-[14px] text-[#0A0A0A] resize-none border-none outline-none placeholder:text-[#9A9A9E] bg-transparent max-h-[120px] leading-[1.4] py-[11px]"
            />
            <button
              type="button"
              aria-label="Emoji"
              className="w-8 h-8 flex items-center justify-center text-[#54656F] active:opacity-50 transition-opacity flex-shrink-0"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                <path d="M8 3.5h8A4.5 4.5 0 0 1 20.5 8v8a4.5 4.5 0 0 1-4.5 4.5H8A4.5 4.5 0 0 1 3.5 16V8A4.5 4.5 0 0 1 8 3.5z" />
                <circle cx="9" cy="10" r="1.1" fill="currentColor" stroke="none" />
                <circle cx="15" cy="10" r="1.1" fill="currentColor" stroke="none" />
                <path d="M8.6 14.2c.9 1.1 2.05 1.7 3.4 1.7s2.5-.6 3.4-1.7" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          {/* Câmera (celular, some quando há texto) — monocromática fora do campo */}
          {isEmpty && (
            <button
              type="button"
              aria-label="Tirar foto da receita"
              onClick={() => cameraInputRef.current?.click()}
              className="w-10 h-11 flex items-center justify-center text-[#0A0A0A] active:opacity-50 transition-opacity flex-shrink-0 md:hidden"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14.5 4.5h-5l-1.7 2.3H5A2.5 2.5 0 0 0 2.5 9.3v7.2A2.5 2.5 0 0 0 5 19h14a2.5 2.5 0 0 0 2.5-2.5V9.3A2.5 2.5 0 0 0 19 6.8h-2.8l-1.7-2.3z" />
                <circle cx="12" cy="12.6" r="3.4" />
              </svg>
            </button>
          )}

          {/* Mic (vazio) ou Enviar (com texto, círculo verde novo) */}
          {isEmpty ? (
            <button
              type="button"
              onClick={handleMicClick}
              aria-label="Gravar áudio"
              className="w-10 h-11 flex items-center justify-center text-[#0A0A0A] active:opacity-50 transition-opacity flex-shrink-0"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="2.5" width="6" height="12" rx="3" />
                <path d="M5 11a7 7 0 0 0 14 0M12 18v3.5" />
              </svg>
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              aria-label="Enviar mensagem"
              className="w-[42px] h-[42px] self-end mb-[1px] flex items-center justify-center rounded-full text-white bg-[#1DAB61] hover:bg-[#179552] active:scale-90 transition-[transform,background-color] duration-150 flex-shrink-0 shadow-[0_1px_3px_rgba(0,0,0,0.15)]"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                <path d="M2 21L23 12L2 3V10L17 12L2 14V21Z" />
              </svg>
            </button>
          )}
        </>
      )}
    </div>
  );
}
